import {
  BC_SALES_ORDER_FILTERS,
  BC_SALES_ORDER_FILTER_LABEL,
  type BcSalesOrderFilter,
} from '@/lib/vision/schemas'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuFilterHeader,
  DropdownMenuFilterTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'

/**
 * "BC SO" filter above the PO queue. Multi-select, same primitives and trigger
 * styling as {@link StatusFilter} so the filter bar stays one idiom.
 *
 * Every label spells out the rule it applies. A control called "SO generated"
 * that quietly meant "at least one wave" would mislead on exactly the POs an
 * operator most needs to see, and the two ambiguous cases each get their own
 * row rather than being folded into a neighbour:
 *
 *   - "Some SOs created" is the half-finished PO. Nothing else on the queue
 *     surfaces it.
 *   - "BC state not known" is Vision having no answer — the flag off, Heimdall
 *     unreachable, or the SO unknown to it. It is deliberately not "No SOs
 *     created": acting on that would mean creating a Sales Order in BC that may
 *     already exist.
 *
 * The trigger carries a count, not the selected label — the chips beside the
 * Status chips do the naming, so the two do not say the same thing twice.
 *
 * Keyboard and focus come from the Radix menu — the trigger is a real button
 * with a visible `focus-visible` ring, arrow keys walk the rows, Enter/Space
 * toggles, Escape closes. Do not copy the queue's sortable column headers here;
 * those have no focus ring today.
 */
export function BcSalesOrderFilterControl({
  value,
  onChange,
}: {
  value: BcSalesOrderFilter[]
  onChange: (next: BcSalesOrderFilter[]) => void
}) {
  const toggle = (option: BcSalesOrderFilter) => {
    if (value.includes(option)) {
      onChange(value.filter(entry => entry !== option))
    } else {
      onChange([...value, option])
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuFilterTrigger
        label="BC SO"
        aria-label="Filter orders by Business Central Sales Order state"
      >
        {value.length > 0 ? (
          <Badge tone="info" className="mr-1.5 tabular-nums">
            {value.length}
          </Badge>
        ) : (
          <span className="mr-1.5 text-[color:var(--foreground)]">Any</span>
        )}
      </DropdownMenuFilterTrigger>
      <DropdownMenuContent align="start" className="min-w-60">
        <DropdownMenuFilterHeader onClear={value.length > 0 ? () => onChange([]) : undefined}>
          Business Central Sales Order
        </DropdownMenuFilterHeader>
        <DropdownMenuSeparator />
        {BC_SALES_ORDER_FILTERS.map(option => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={value.includes(option.value)}
            onCheckedChange={() => toggle(option.value)}
            onSelect={event => event.preventDefault()}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Selected-value chips, matching the Status chips beside them. */
export function BcSalesOrderFilterChips({ value }: { value: BcSalesOrderFilter[] }) {
  if (value.length === 0) return null
  return (
    <>
      {value.map(option => (
        <Badge key={option} tone="info">
          {BC_SALES_ORDER_FILTER_LABEL[option]}
        </Badge>
      ))}
    </>
  )
}
